import React, { useState, useEffect, useContext } from "react";

import * as Sentry from "@sentry/react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import {
  Grid,
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Hidden,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import queryString from "query-string";
import { MdContentCopy } from "react-icons/md";
import { REACT_APP_PROD_URL } from "../../services/configs";
import ImageWebp from "../../components/ImageWebp";
import Nominee from "../../components/Nominee";

import { CoinContext } from "../../contexts/CoinContext";
import { useSelector } from "react-redux";
import fetcher from "../../api/swr_fetcher";
import { copyTextAndSendToApp } from "../../services/appMethods";

export default function Settings() {
  const classes = useStyles();
  const theme = useTheme();
  let history = useHistory();

  const token = useSelector((state) => state.trading.TOKEN);
  const loginId = useSelector((state) => state.trading.LOGIN_ID);

  const [enableView, setEnableView] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [qrcode, setQrcode] = useState(null);
  const [twoFACode, setTwoFACode] = useState(null);
  const [openDialog, setDialog] = useState(false);

  const [error2FACode, setError2FACode] = useState(false);

  const { handleClickAlert } = useContext(CoinContext);

  useEffect(() => {
    if (loginId) {
      fetcher(`/isUsing2FA?login=${loginId}`).then((data) => {
        // console.log(`Two factor auth: ${data}`);
        setTwoFactorEnabled(data);
      });
    }
  }, []);

  function get2FAqrcode() {
    if (token) {
      fetcher(`/v1/um/2FA`)
        .then((data) => {
          if (data) {
            setQrcode(data.qrCode);
            setDialog(true);
          } else {
            handleClickAlert("error", "Request failed, please try again");
          }
          // console.log(data);
        })
        .catch((err) => {
          handleClickAlert("error", "Request failed, please try again");

          console.error(err);
          // console.error(err);
        });

      // console.log("Getting 2FA qrcode");
    }
  }

  function enableAuthentication() {
    if (token && twoFACode) {
      fetcher(`/v1/um/2FA`, {
        method: "PUT",
        body: {
          enableTwoFA: true,
          verificationCode: twoFACode,
        },
      })
        .then((res) => {
          setTwoFactorEnabled(true);
          setDialog(false);
          setTwoFACode(null);
          setError2FACode(false);
          handleClickAlert("success", "Two factor authentication enabled");
        })

        .catch((err) => {
          handleClickAlert("error", "Request failed, please try again");

          console.error(err);
          // console.error(err);
        });

      // console.log("Enabling Auth");
    } else {
      setError2FACode(true);
    }
  }

  function disableAuthentication() {
    if (token && twoFACode) {
      fetcher(`/v1/um/2FA`, {
        method: "PUT",
        body: { enableTwoFA: false, verificationCode: twoFACode },
      })
        .then((res) => {
          setTwoFactorEnabled(false);
          setDialog(false);
          setTwoFACode(null);
          setError2FACode(false);
          handleClickAlert("success", "Two factor authentication disabled");
        })
        .catch((err) => {
          handleClickAlert("error", "Request failed, please try again");

          console.error(err);
          // console.error(err);
        });

      // console.log("Disabling Auth");
    } else {
      setError2FACode(true);
    }
  }

  function getSecretAuth(strCode) {
    let ss = strCode.slice(strCode.indexOf("secret") + 9);
    return ss.slice(0, ss.indexOf("%"));
  }

  return (
    <div className={classes.root}>
      <Hidden smDown>
        <p className={classes.topHeading}>Security</p>
      </Hidden>

      <Grid container className={classes.paddedContainer}>
        <Grid item xs={12}>
          <Hidden mdUp>
            <Box>
              <Typography
                variant="h4"
                component="h4"
                className={classes.stepHeading}
              >
                Security
              </Typography>
            </Box>
          </Hidden>

          {/*Two Factor Authentication*/}
          <Box mt={3}>
            <Box
              pt={3}
              pb={3}
              className={`${classes.twoFactorContainer}`}
              display="flex"
              justifyContent="space-between"
              flexWrap="wrap"
              alignItems="center"
            >
              <Typography className={classes.labelText}>
                Two Factor Authentication
              </Typography>

              <div style={{ display: "flex" }}>
                <Button
                  onClick={() => {
                    setEnableView(true);
                    get2FAqrcode();
                  }}
                  className={classes.buttonStyle}
                  variant="contained"
                  color="primary"
                  disabled={twoFactorEnabled ? true : false}
                >
                  Enable
                </Button>
                <Button
                  onClick={() => {
                    setEnableView(false);
                    setDialog(true);
                  }}
                  className={classes.buttonStyle}
                  style={{ marginLeft: "10px" }}
                  variant="contained"
                  color="primary"
                  disabled={twoFactorEnabled ? false : true}
                >
                  Disable
                </Button>
              </div>
            </Box>
          </Box>

          {/*Change password*/}
          <Box mt={3}>
            <Box
              className={classes.twoFactorContainer}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography className={classes.labelText}>
                Change Password
              </Typography>
              <Button
                className={classes.buttonStyle}
                color="primary"
                variant="contained"
                onClick={() => {
                  // console.log("Logging out");

                  history.push(`/forgotpassword`);
                }}
              >
                Change
              </Button>
            </Box>
          </Box>

          <Hidden mdUp>
            <Nominee />
          </Hidden>
        </Grid>
      </Grid>

      <Dialog
        open={openDialog}
        onClose={() => {
          setDialog(false);
        }}
        // fullWidth={true}
        maxWidth="sm"
      >
        <DialogTitle>{"Two Factor Authentication"}</DialogTitle>
        <Divider />
        <DialogContent>
          {enableView ? (
            <Grid container>
              <Grid item xs={12} md={6} style={{ paddingLeft: "12px" }}>
                <DialogTitle style={{ paddingLeft: "0px" }}>
                  {"QR Code"}
                </DialogTitle>
                <ImageWebp src={qrcode} />
                <DialogContentText
                  style={{ marginTop: "12px", marginBottom: 0 }}
                >
                  Authentication code for manual entry
                </DialogContentText>

                <DialogContentText color="primary">
                  {qrcode ? (
                    <div style={{ display: "flex" }}>
                      <p>{getSecretAuth(qrcode)}</p>
                      <div style={{ flexGrow: 1 }} />
                      <Tooltip
                        title="copy authentication code"
                        placement="right"
                      >
                        <IconButton
                          style={{ padding: 0, marginLeft: "10px" }}
                          className={classes.copyIcon}
                          onClick={() => {
                            navigator.clipboard.writeText(
                              getSecretAuth(qrcode)
                            );
                            copyTextAndSendToApp(getSecretAuth(qrcode));

                            handleClickAlert(
                              "success",
                              "Authentication code copied!"
                            );
                          }}
                        >
                          {/* <InfoOutlinedIcon fontSize="small" /> */}
                          <MdContentCopy />
                        </IconButton>
                      </Tooltip>
                    </div>
                  ) : (
                    "Loading..."
                  )}
                </DialogContentText>

                <Divider />

                <DialogContentText
                  style={{
                    fontSize: "20px",
                    marginTop: "20px",
                    marginBottom: 0,
                  }}
                >
                  Verification
                </DialogContentText>
                <Typography
                  variant="body1"
                  component="p"
                  style={{
                    marginTop: "10px",
                    fontSize: "15px",
                    fontWeight: "bold",
                    color: theme.palette.text.primary,
                    opacity: "0.5",
                  }}
                >
                  CODE
                </Typography>
                <Box mt={1} mb={4}>
                  <TextField
                    fullWidth={true}
                    name="verification_code"
                    required
                    className={classes.textFieldRoot}
                    InputProps={{
                      classes: { root: classes.textFieldStyles },
                    }}
                    variant="outlined"
                    onChange={(e) => {
                      let val = e.target.value.replace(/[^0-9]$/, "");
                      setTwoFACode(val);
                    }}
                    error={error2FACode}
                    helperText={error2FACode ? "Please enter the OTP" : null}
                  />
                </Box>
                <DialogActions
                  style={{ display: "block", padding: 0, marginBottom: "20px" }}
                >
                  <Button
                    style={{ color: "white", fontWeight: "bold" }}
                    variant="contained"
                    onClick={() => {
                      enableAuthentication();
                      setDialog(false);
                    }}
                    color="primary"
                    autoFocus
                  >
                    Enable
                  </Button>
                </DialogActions>
              </Grid>

              <div className="container-bt">
                <Grid item xs={12} md={6}>
                  <DialogContentText>
                    <ol>
                      <li>
                        Install an authenticator app (Google authenticator /
                        Authy , etc.) on your mobile device if you don’t already
                        have one.
                      </li>
                      <li>
                        Scan QR / Manually enter the authentication code in your
                        authenticator app.
                      </li>
                      <li>
                        Enter the verification code prompted in your app. Click
                        to enable.
                      </li>
                    </ol>
                  </DialogContentText>
                </Grid>
              </div>
            </Grid>
          ) : (
            <>
              <DialogContentText style={{ marginTop: "12px", marginBottom: 0 }}>
                Enter OTP from authenticator to disable two factor
                authentication.
              </DialogContentText>
              <Typography
                variant="body1"
                component="p"
                style={{
                  marginTop: "10px",
                  fontSize: "15px",
                  fontWeight: "bold",
                  color: theme.palette.text.primary,
                  opacity: "0.5",
                }}
              >
                OTP
              </Typography>
              <Box mt={1} mb={4}>
                <TextField
                  fullWidth={true}
                  name="verification_code"
                  required
                  className={classes.textFieldRoot}
                  InputProps={{
                    classes: { root: classes.textFieldStyles },
                  }}
                  variant="outlined"
                  onChange={(e) => {
                    let val = e.target.value.replace(/[^0-9]$/, "");
                    setTwoFACode(val);
                  }}
                  error={error2FACode}
                  helperText={error2FACode ? "Please enter the OTP" : null}
                />
              </Box>
              <DialogActions
                style={{ display: "block", padding: 0, marginBottom: "20px" }}
              >
                <Button
                  style={{ color: "white", fontWeight: "bold" }}
                  variant="contained"
                  onClick={disableAuthentication}
                  color="primary"
                  autoFocus
                >
                  Disable
                </Button>
              </DialogActions>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    // marginTop: theme.spacing(10),
    // marginLeft: theme.spacing(10),
    // alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      // width: '90%',
      marginTop: "20px",
      // maxWidth: "450px",
      // minWidth: "450px",
      // marginLeft: "25px",
      // marginRight: "25px",
    },
    [theme.breakpoints.up("md")]: {
      // width: "50%",
      marginTop: "50px",
    },
    // margin: "auto",
    marginBottom: "80px",
  },
  paddedContainer: {
    padding: "40px",
    background: theme.palette.type === "dark" ? "#151827" : "white",
  },
  topHeading: {
    color: "#3AC4AB",
    fontWeight: "bold",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.3rem",
    },
    [theme.breakpoints.up("sm")]: {
      fontSize: "2rem",
    },
  },
  twoFactorContainer: {
    border: `1px solid #3AC4AB`,
    backgroundColor: "rgba(86,195,164,0.1)",
    padding: "6px 12px",
    borderRadius: "2px",
  },
  labelText: {
    fontWeight: "bold",
    color: theme.palette.text.primary,
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8rem",
    },
    [theme.breakpoints.up("sm")]: {
      fontSize: "1rem",
    },
  },
  stepHeading: {
    color: theme.palette.text.primary,
    fontWeight: "bold",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.3rem",
    },
    [theme.breakpoints.up("sm")]: {
      fontSize: "1.5625rem",
    },
  },
  stepSubHeading: {
    color: theme.palette.text.primary,
    opacity: 0.6,
    letterSpacing: 0.3,
  },
  textFieldRoot: {
    "& > p": {
      color: theme.palette.text.primary,
      opacity: 0.5,
      fontSize: "13px",
      lineHeight: "16px",
    },
  },
  textFieldStyles: {
    border: `1px solid ${theme.palette.text.primary}40`,
    borderRadius: 3,
    "&::before": {
      display: "none",
    },
    "& input": {
      padding: "12px 14px",
      color: theme.palette.text.primary,
      fontWeight: "bold",
      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
        "-webkit-appearance": "none",
        margin: 0,
      },
    },
  },
  fieldLabel: {
    fontWeight: "bold",
    color: theme.palette.text.primary,
    opacity: "0.5",
    textTransform: "uppercase",
    marginTop: "17px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1rem",
    },
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    color: "#fff",
    fontWeight: "600",
    width: "150px",
  },
  buttonStyle: {
    color: "#fff",
    fontWeight: "bold",
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
      padding: "3px 13px",
    },
  },
}));
